import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Server, Globe, ExternalLink, Loader2, ChevronDown, Cpu } from "lucide-react";
import clienteApi from "@/lib/clienteApi";

/**
 * Modal de Configuração do Servidor de Acesso (único registro por cliente).
 *
 * - GET /api/cliente/servidor → preenche o formulário (ou vazio se nunca cadastrado).
 * - PUT /api/cliente/servidor → upsert (1 doc por cliente_id, não cria múltiplos).
 *
 * Acesso Remoto:
 *  - "anydesk"   → abre via `anydesk:{id}` (link nativo, registrado pelo SO).
 *  - "hoptodesk" → abre janela `web.hoptodesk.com` com `target="hoptodesk-session"`
 *                  (sempre a mesma janela — não acumula abas).
 */
const ESP32_URL_DEFAULT = "https://app.braveaccess.com.br/api/iot/heartbeat";
const ESP32_POLLING_DEFAULT_MS = 30000;
const ESP32_POLLING_MIN_MS = 500;

export default function ConfigServidorModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [esp32Open, setEsp32Open] = useState(false);
  const [form, setForm] = useState({
    ip: "",
    mascara: "",
    gateway: "",
    diversos: "",
    acesso_remoto: { aplicacao: "anydesk", id_remoto: "", senha: "" },
    placa_esp32_url: ESP32_URL_DEFAULT,
    placa_esp32_polling_ms: ESP32_POLLING_DEFAULT_MS,
    // v0.10.3 — Threshold "device offline" configurável por tenant.
    // null = usar default global do backend (90s). Valores válidos: 60/90/120.
    iot_online_threshold_sec: null,
  });

  // Carrega o registro único quando o modal abre.
  useEffect(() => {
    if (!open) return;
    setEsp32Open(false);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await clienteApi.get("/cliente/servidor");
        if (cancelled) return;
        const d = res.data || {};
        setForm({
          ip: d.ip || "",
          mascara: d.mascara || "",
          gateway: d.gateway || "",
          diversos: d.diversos || "",
          acesso_remoto: {
            aplicacao: d.acesso_remoto?.aplicacao || "anydesk",
            id_remoto: d.acesso_remoto?.id_remoto || "",
            senha: d.acesso_remoto?.senha || "",
          },
          // Default explícito p/ apontar para a Cloud em produção; se o cliente
          // tiver outra Cloud (preview / on-prem) sobrescreve manualmente.
          placa_esp32_url: d.placa_esp32_url || ESP32_URL_DEFAULT,
          placa_esp32_polling_ms: (() => {
            const v = parseInt(d.placa_esp32_polling_ms, 10);
            if (!Number.isFinite(v) || v < ESP32_POLLING_MIN_MS) return ESP32_POLLING_DEFAULT_MS;
            return v;
          })(),
          iot_online_threshold_sec:
            [60, 90, 120].includes(d.iot_online_threshold_sec) ? d.iot_online_threshold_sec : null,
        });
      } catch {
        if (!cancelled) toast.error("Erro ao carregar dados do servidor");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const salvar = async () => {
    setSaving(true);
    try {
      await clienteApi.put("/cliente/servidor", form);
      toast.success("Servidor salvo com sucesso");
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Erro ao salvar";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const abrirAcessoRemoto = () => {
    const { aplicacao, id_remoto } = form.acesso_remoto;
    const id = (id_remoto || "").trim();
    if (aplicacao === "anydesk") {
      if (!id) { toast.error("Informe o ID do AnyDesk"); return; }
      // Link nativo do AnyDesk — disparado pelo handler de protocolo do SO.
      window.location.href = `anydesk:${id}`;
    } else if (aplicacao === "hoptodesk") {
      // Janela nomeada — reaproveita a mesma se já estiver aberta.
      window.open("https://web.hoptodesk.com", "hoptodesk-session");
    }
  };

  if (!open) return null;

  // Padrão de glass do projeto (mesmo da /cliente login):
  //   container: `glass-card bg-white/10 backdrop-blur-xl border-white/15`
  //   inputs:    `bg-white/10 border-white/15 text-white placeholder:text-brand-ice/60`
  const labelCls = "text-brand-ice/90 text-[10px] uppercase tracking-wider font-heading font-bold text-shadow-label";
  const inputCls =
    "w-full bg-white/10 border border-white/15 rounded-md px-3 h-10 text-sm text-white " +
    "placeholder:text-brand-ice/40 focus:outline-none focus:border-brand-light focus:ring-1 focus:ring-brand-light/20 transition";
  // Para o <select>: as <option> nativas usam o background do <select> em alguns
  // navegadores (Firefox) e o background do SO em outros (Chrome). Forçamos
  // `bg-brand-dark` via classe no <option> para legibilidade em ambos.
  const selectCls = inputCls + " appearance-none cursor-pointer pr-8";

  return (
    <div
      data-testid="config-servidor-modal"
      className="fixed inset-0 z-50 bg-brand-medium/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="glass-card bg-white/10 backdrop-blur-xl border border-white/15 rounded-xl max-w-3xl w-full overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-white/[0.03]">
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-sky-300" />
            <h3 className="text-lg font-bold text-white font-heading text-shadow-logo">
              Configuração do Servidor de Acesso
            </h3>
          </div>
          <button
            data-testid="config-servidor-close"
            onClick={() => onClose?.()}
            className="w-8 h-8 flex items-center justify-center rounded text-brand-ice/60 hover:bg-white/10 hover:text-white transition"
            aria-label="Fechar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-brand-ice/70">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando...
            </div>
          ) : (
            <>
              {/* ── Dados do Servidor ── */}
              <section>
                <h4 className="text-sm font-heading font-bold text-brand-ice mb-3 flex items-center gap-2 text-shadow-label">
                  <Globe className="w-4 h-4 text-sky-300" /> Dados do Servidor
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className={labelCls}>IP</label>
                    <input
                      data-testid="servidor-ip-input"
                      type="text"
                      value={form.ip}
                      onChange={(e) => setForm((f) => ({ ...f, ip: e.target.value }))}
                      placeholder="192.168.0.10"
                      className={inputCls + " font-mono"}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Máscara</label>
                    <input
                      data-testid="servidor-mascara-input"
                      type="text"
                      value={form.mascara}
                      onChange={(e) => setForm((f) => ({ ...f, mascara: e.target.value }))}
                      placeholder="255.255.255.0"
                      className={inputCls + " font-mono"}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Gateway</label>
                    <input
                      data-testid="servidor-gateway-input"
                      type="text"
                      value={form.gateway}
                      onChange={(e) => setForm((f) => ({ ...f, gateway: e.target.value }))}
                      placeholder="192.168.0.1"
                      className={inputCls + " font-mono"}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label className={labelCls}>Diversos</label>
                  <textarea
                    data-testid="servidor-diversos-input"
                    value={form.diversos}
                    onChange={(e) => setForm((f) => ({ ...f, diversos: e.target.value }))}
                    rows={4}
                    placeholder="Textos complementares sobre o servidor (observações, credenciais de rede, particularidades, etc.)"
                    className={inputCls.replace("h-10", "min-h-[96px] py-2") + " resize-y leading-relaxed"}
                  />
                </div>
              </section>

              {/* ── Acesso Remoto ── */}
              <section className="pt-5 border-t border-white/10">
                <h4 className="text-sm font-heading font-bold text-brand-ice mb-3 flex items-center gap-2 text-shadow-label">
                  <ExternalLink className="w-4 h-4 text-sky-300" /> Acesso Remoto
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr_1fr_auto] gap-3 items-end">
                  <div className="relative">
                    <label className={labelCls}>Aplicação</label>
                    <select
                      data-testid="servidor-aplicacao-select"
                      value={form.acesso_remoto.aplicacao}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          acesso_remoto: { ...f.acesso_remoto, aplicacao: e.target.value },
                        }))
                      }
                      className={selectCls}
                    >
                      {/* `bg-brand-dark` força o fundo do dropdown nativo no Chrome/Edge */}
                      <option value="anydesk" className="bg-brand-dark text-white">AnyDesk</option>
                      <option value="hoptodesk" className="bg-brand-dark text-white">HopToDesk</option>
                    </select>
                    <ChevronDown className="absolute right-2 bottom-3 w-4 h-4 text-brand-ice/50 pointer-events-none" />
                  </div>
                  <div>
                    <label className={labelCls}>ID</label>
                    <input
                      data-testid="servidor-id-input"
                      type="text"
                      value={form.acesso_remoto.id_remoto}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          acesso_remoto: { ...f.acesso_remoto, id_remoto: e.target.value },
                        }))
                      }
                      placeholder="123 456 789"
                      className={inputCls + " font-mono"}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Senha</label>
                    <input
                      data-testid="servidor-senha-input"
                      type="text"
                      value={form.acesso_remoto.senha}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          acesso_remoto: { ...f.acesso_remoto, senha: e.target.value },
                        }))
                      }
                      placeholder="(opcional)"
                      className={inputCls}
                    />
                  </div>
                  <button
                    data-testid="servidor-abrir-btn"
                    type="button"
                    onClick={abrirAcessoRemoto}
                    className="h-10 px-4 rounded-md bg-brand-medium hover:bg-brand-dark border border-white/15 text-white text-sm font-heading font-bold transition flex items-center gap-1.5 shadow-lg"
                    title={
                      form.acesso_remoto.aplicacao === "anydesk"
                        ? "Abre o AnyDesk instalado no sistema (anydesk:ID)"
                        : "Abre o HopToDesk Web em janela dedicada"
                    }
                  >
                    <ExternalLink className="w-4 h-4" /> Abrir
                  </button>
                </div>
                <p className="text-[11px] text-brand-ice/60 mt-2">
                  {form.acesso_remoto.aplicacao === "anydesk" ? (
                    <>O AnyDesk precisa estar instalado na estação para que o link <code className="text-sky-300/80">anydesk:ID</code> funcione.</>
                  ) : (
                    <>O HopToDesk Web abre em <code className="text-sky-300/80">https://web.hoptodesk.com</code> — sempre na mesma janela.</>
                  )}
                </p>
              </section>

              {/* ── Placa ESP32 (collapsible, fechado por padrão) ── */}
              <section className="pt-5 border-t border-white/10">
                <button
                  type="button"
                  data-testid="servidor-esp32-trigger"
                  onClick={() => setEsp32Open((v) => !v)}
                  aria-expanded={esp32Open}
                  className="w-full flex items-center gap-2 text-sm font-heading font-bold text-brand-ice text-shadow-label"
                >
                  <Cpu className="w-4 h-4 text-sky-300" /> Placa ESP32
                  <ChevronDown className={`w-4 h-4 text-brand-ice/50 ml-auto transition-transform ${esp32Open ? "rotate-180" : ""}`} />
                </button>
                {esp32Open && (
                <div data-testid="servidor-esp32-content" className="mt-3">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-3 items-start">
                  <div>
                    <label className={labelCls}>URL</label>
                    <input
                      data-testid="servidor-esp32-url-input"
                      type="url"
                      value={form.placa_esp32_url}
                      onChange={(e) => setForm((f) => ({ ...f, placa_esp32_url: e.target.value }))}
                      title="Endpoint para post de heartbeat e Keep Alive"
                      alt="Endpoint para post de heartbeat e Keep Alive"
                      placeholder={ESP32_URL_DEFAULT}
                      className={inputCls + " font-mono text-[13px]"}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Polling (ms)</label>
                    <input
                      data-testid="servidor-esp32-polling-input"
                      type="number"
                      min={ESP32_POLLING_MIN_MS}
                      step={500}
                      value={form.placa_esp32_polling_ms}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setForm((f) => ({
                          ...f,
                          placa_esp32_polling_ms: Number.isFinite(v) ? v : ESP32_POLLING_DEFAULT_MS,
                        }));
                      }}
                      onBlur={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isFinite(v) || v < ESP32_POLLING_MIN_MS) {
                          setForm((f) => ({ ...f, placa_esp32_polling_ms: ESP32_POLLING_MIN_MS }));
                        }
                      }}
                      title={`Intervalo do polling em milissegundos (mínimo ${ESP32_POLLING_MIN_MS}ms). Default: ${ESP32_POLLING_DEFAULT_MS}ms.`}
                      className={inputCls + " font-mono text-[13px]"}
                    />
                  </div>
                </div>
                <p className="text-[11px] text-brand-ice/60 mt-2">
                  Endpoint para post de heartbeat e Keep Alive das placas ESP32.
                  Default URL: <code className="text-sky-300/80">{ESP32_URL_DEFAULT}</code> ·
                  Polling default: <code className="text-sky-300/80">{ESP32_POLLING_DEFAULT_MS}ms</code>
                  (mín. <code className="text-sky-300/80">{ESP32_POLLING_MIN_MS}ms</code>).
                </p>

                {/* v0.10.3 — Threshold "device offline" por tenant */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <label className={labelCls}>
                    Alarme OFF-LINE — tempo máximo sem heartbeat
                  </label>
                  <select
                    data-testid="servidor-esp32-threshold-select"
                    value={form.iot_online_threshold_sec ?? ""}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setForm((f) => ({
                        ...f,
                        iot_online_threshold_sec: raw === "" ? null : parseInt(raw, 10),
                      }));
                    }}
                    className={inputCls}
                    title="Tempo (em segundos) que uma placa ESP32 pode ficar sem enviar heartbeat antes de ser marcada OFFLINE em /iot e disparar o Alarme de Sincronização em /cliente/dashboard."
                  >
                    <option value="">Default global (90s)</option>
                    <option value="60">60s — Agressivo (detecta em ~30-60s, +ruído)</option>
                    <option value="90">90s — Balanceado (recomendado)</option>
                    <option value="120">120s — Conservador (menos falsos-positivos)</option>
                  </select>
                  <p className="text-[11px] text-brand-ice/60 mt-1">
                    Com polling de {form.placa_esp32_polling_ms}ms, a placa envia ~{Math.round(60000 / (form.placa_esp32_polling_ms || 30000))} heartbeats/min.
                    Threshold recomendado = 2× polling + margem = <code className="text-sky-300/80">~90s</code>.
                  </p>
                </div>
                </div>
                )}
              </section>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2 bg-white/[0.03]">
          <button
            data-testid="servidor-cancelar-btn"
            onClick={() => onClose?.()}
            disabled={saving}
            className="px-4 h-10 rounded-md bg-white/5 hover:bg-white/10 border border-white/15 text-brand-ice/80 hover:text-white text-sm transition"
          >
            Cancelar
          </button>
          <button
            data-testid="servidor-salvar-btn"
            onClick={salvar}
            disabled={saving || loading}
            className="px-5 h-10 rounded-md bg-brand-medium hover:bg-brand-dark border border-white/15 text-white text-sm font-heading font-bold transition flex items-center gap-2 disabled:opacity-50 shadow-lg"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
